import { useEffect, useMemo, useRef, useState } from 'react'

import _ from 'lodash'

import { createValidationSchema } from '../../../helpers/validationSchemas'
import { useCreateActionContext } from '../../../contexts/action'
import { useGetActionTypesQuery, useGetCustomFieldsQuery } from '../../../services/field'

export function useCustomFields({ values, errors, setValues, actionTypeId }) {
  const [formValues, setFormValues] = useState({})
  const [visibleGroups, setVisibleGroups] = useState({})
  const [initialized, setInitialized] = useState(false)

  const {
    currentUser: { parentMember },
    isReadOnly,
    isPreview,
    setValidationSchema
  } = useCreateActionContext()

  const { data: customFields, isLoading } = useGetCustomFieldsQuery({ actionTypeId }, { skip: !actionTypeId })
  const { data: actionTypesList } = useGetActionTypesQuery({ ...(parentMember && { memberIds: parentMember.id }) })

  const debouncedSetValues = useRef(_.debounce(setValues, 200)).current

  useEffect(() => () => debouncedSetValues.cancel(), [debouncedSetValues])

  function onChangeValue(values) {
    const newValues = { ...formValues, ...values }
    setFormValues(newValues)
    debouncedSetValues('customFields', newValues)
  }

  useEffect(
    function updateFormValues() {
      if (Object.keys(formValues).length > 0) return
      setFormValues(values)
    },
    [values]
  )

  useEffect(
    function updateVisibleGroups() {
      if (customFields && Object.keys(visibleGroups).length === 0) {
        const visibleFieldGroups = {
          ungrouped: { groups: [], currentIndex: -1 }
        }

        customFields.forEach(({ isGroup, isGroupUnderGroup, groupType, fields }, fieldName) => {
          const groupKey = isGroup && isGroupUnderGroup ? groupType : 'ungrouped'

          if (!visibleFieldGroups[groupKey]) {
            visibleFieldGroups[groupKey] = { groups: [], currentIndex: 0 }
          }

          const currentGroup = visibleFieldGroups[groupKey]
          currentGroup.groups.push({
            fieldName,
            ...(isGroup && isGroupUnderGroup
              ? { internalFieldNames: fields.map(({ internalFieldName }) => internalFieldName) }
              : {})
          })

          if (groupKey === 'ungrouped') {
            currentGroup.currentIndex++
          }
        })

        setVisibleGroups(visibleFieldGroups)
      }
    },
    [customFields, values]
  )

  useEffect(
    function initializeFormValuesWithVisibleGroups() {
      if (!initialized && Object.keys(visibleGroups).length > 0) {
        const formValuesKeys = Object.keys(values)
        const groupTypes = { ...visibleGroups }

        Object.keys(visibleGroups).forEach((groupType) => {
          if (groupType === 'ungrouped') return

          const { groups } = visibleGroups[groupType]
          groups.forEach(({ internalFieldNames, fieldName }) => {
            const enableGroup = internalFieldNames.some((fieldName) => formValuesKeys.includes(fieldName))
            if (enableGroup) {
              const groupIndex = groups.findIndex((group) => group.fieldName === fieldName)
              groupTypes[groupType] = { groups, currentIndex: groupIndex }
            }
          })
        })

        setVisibleGroups(groupTypes)
        setInitialized(true)
      }
    },
    [values, visibleGroups, initialized]
  )

  useEffect(
    function updateValidationSchema() {
      if (Object.keys(visibleGroups).length === 0) return

      const allFields = Object.values(visibleGroups).flatMap(({ groups, currentIndex }) =>
        groups.slice(0, currentIndex + 1).map(({ fieldName }) => customFields.get(fieldName)?.fields)
      )

      const updatedValidationSchema = createValidationSchema(allFields.flat())
      setValidationSchema(updatedValidationSchema)
    },
    [visibleGroups]
  )

  useEffect(
    function scrollToFirstError() {
      if (errors) {
        const errorKeys = Object.keys(errors)
        if (errorKeys.length > 0) {
          const firstErrorElement = document.getElementById(`${errorKeys[0]}`)
          if (firstErrorElement) {
            firstErrorElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
            firstErrorElement.focus()
          }
        }
      }
    },
    [errors]
  )

  const actionName = actionTypeId && actionTypesList?.find(({ value }) => value === actionTypeId)?.description

  const setGroupVisible = (groupType) => {
    setVisibleGroups((prevVisibleGroups) => ({
      ...prevVisibleGroups,
      [groupType]: {
        ...prevVisibleGroups[groupType],
        currentIndex: prevVisibleGroups[groupType].currentIndex + 1
      }
    }))
  }

  const setGroupHidden = (groupType) => {
    const { groups, currentIndex } = visibleGroups[groupType]
    const targetGroup = groups[currentIndex]

    if (targetGroup.internalFieldNames) {
      const newValues = { ...formValues }
      targetGroup.internalFieldNames.forEach((internalFieldName) => {
        newValues[internalFieldName] = null
      })

      setFormValues(newValues)
      debouncedSetValues('customFields', newValues)
    }

    setVisibleGroups((prevVisibleGroups) => ({
      ...prevVisibleGroups,
      [groupType]: {
        ...prevVisibleGroups[groupType],
        currentIndex: currentIndex - 1
      }
    }))
  }

  const visibleFields = useMemo(() => {
    if (Object.keys(visibleGroups).length === 0) return []

    const visible = Object.values(visibleGroups)
      .map(({ groups, currentIndex }) => groups.slice(0, currentIndex + 1).map(({ fieldName }) => fieldName))
      .flat()

    return Array.from(customFields.entries()).filter(([fieldName]) => visible.includes(fieldName))
  }, [visibleGroups, customFields])

  return {
    formValues,
    setFormValues,
    visibleGroups,
    setVisibleGroups,
    initialized,
    setInitialized,
    customFields,
    isLoading,
    actionTypesList,
    debouncedSetValues,
    onChangeValue,
    actionName,
    setGroupVisible,
    setGroupHidden,
    visibleFields,
    isReadOnly,
    isPreview
  }
}
